import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import GppMaybeRoundedIcon from '@mui/icons-material/GppMaybeRounded';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { loginRequest } from '../authConfig';

const initialImportTemplate = `nrn,decision,comments
2026100001,Cleared,No disqualifying issue found
2026100002,Rejected,Police objection recorded`;

function Home({ permissions = [], displayName = '' }) {
  const { instance, accounts } = useMsal();
  const [dashboard, setDashboard] = useState(null);
  const [workbench, setWorkbench] = useState(null);
  const [selectionResults, setSelectionResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [workbenchLoading, setWorkbenchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [exportBatch, setExportBatch] = useState(null);
  const [importBatchId, setImportBatchId] = useState('');
  const [importCsv, setImportCsv] = useState(initialImportTemplate);
  const [importSharedSecret, setImportSharedSecret] = useState('');
  const [vettingDrafts, setVettingDrafts] = useState({});

  const getAccessToken = async () => {
    const account = accounts[0];
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account
    });

    return tokenResponse.accessToken;
  };

  const apiFetch = async (url, options = {}) => {
    const accessToken = await getAccessToken();
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  };

  const loadDashboard = async () => {
    setDashboardLoading(true);

    try {
      const data = await apiFetch('/api/dashboard', { headers: {} });
      setDashboard(data);
    } catch (err) {
      console.error('Error loading dashboard', err);
      setError(err.message || 'Unable to load dashboard');
    } finally {
      setDashboardLoading(false);
    }
  };

  const loadWorkbench = async () => {
    setWorkbenchLoading(true);

    try {
      const data = await apiFetch('/api/police-workbench', { headers: {} });
      setWorkbench(data);
      if (!importBatchId && data.recentBatches?.length) {
        setImportBatchId(data.recentBatches[0].batchId);
      }
    } catch (err) {
      console.error('Error loading workbench', err);
      setError(err.message || 'Unable to load police workbench');
    } finally {
      setWorkbenchLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadDashboard(), loadWorkbench()]);
  };

  const downloadBatchCsv = async (batchId) => {
    setActionLoading(`download-${batchId}`);
    setError('');

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`/api/police-exports/${encodeURIComponent(batchId)}/download`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        let message = 'Unable to download export batch';
        try {
          const data = await response.json();
          message = data.error || message;
        } catch (error) {
          // Ignore JSON parsing failure for non-JSON error responses.
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${batchId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Error downloading export batch', err);
      setError(err.message || 'Unable to download export batch');
    } finally {
      setActionLoading('');
    }
  };

  const runSelection = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await apiFetch('/api/run-selection', {
        method: 'POST',
        body: JSON.stringify({})
      });
      setSelectionResults(data);
      setExportBatch(null);
      setSuccess(`Generated ${data.length} potential jurors for the latest pool.`);
      await refreshAll();
    } catch (err) {
      console.error('Error running selection', err);
      setError(err.message || 'Unable to generate jury pool');
    } finally {
      setLoading(false);
    }
  };

  const createExportBatch = async () => {
    setActionLoading('export');
    setError('');
    setSuccess('');

    try {
      const data = await apiFetch('/api/police-exports', {
        method: 'POST',
        body: JSON.stringify({ runId: workbench?.latestRun?.runId })
      });
      setExportBatch(data);
      setImportBatchId(data.batchId);
      setSuccess(`Created export batch ${data.batchId} with ${data.count} registrants. Checksum ${data.exportChecksum.slice(0, 12)}…`);
      await refreshAll();
    } catch (err) {
      console.error('Error creating export batch', err);
      setError(err.message || 'Unable to create export batch');
    } finally {
      setActionLoading('');
    }
  };

  const applyInAppDecision = async (nrn, decision) => {
    setActionLoading(`vet-${nrn}-${decision}`);
    setError('');
    setSuccess('');

    try {
      const draft = vettingDrafts[nrn] || {};
      await apiFetch('/api/police-vetting', {
        method: 'POST',
        body: JSON.stringify({
          runId: workbench?.latestRun?.runId,
          nrn,
          decision,
          comments: draft.comments || ''
        })
      });
      setSuccess(`Applied ${decision.toLowerCase()} decision for ${nrn}.`);
      setVettingDrafts((current) => {
        const next = { ...current };
        delete next[nrn];
        return next;
      });
      await refreshAll();
    } catch (err) {
      console.error('Error applying vetting decision', err);
      setError(err.message || 'Unable to apply police decision');
    } finally {
      setActionLoading('');
    }
  };

  const importBatchResults = async () => {
    setActionLoading('import');
    setError('');
    setSuccess('');

    try {
      const data = await apiFetch('/api/police-import', {
        method: 'POST',
        body: JSON.stringify({
          batchId: importBatchId,
          csvData: importCsv,
          sharedSecret: importSharedSecret
        })
      });
      setSuccess(`Imported ${data.processed} police decisions from batch ${data.batchId}. Status: ${data.reconciliationStatus}.`);
      await refreshAll();
    } catch (err) {
      console.error('Error importing batch results', err);
      setError(err.message || 'Unable to import batch results');
    } finally {
      setActionLoading('');
    }
  };

  const handleDraftChange = (nrn, field, value) => {
    setVettingDrafts((current) => ({
      ...current,
      [nrn]: {
        ...current[nrn],
        [field]: value
      }
    }));
  };

  const summaryCards = dashboard?.summaryCards || [];
  const policyHighlights = dashboard?.policyHighlights || [];
  const latestPool = dashboard?.latestPool;
  const recentActivity = dashboard?.recentActivity || [];
  const pendingRegistrants = workbench?.pendingRegistrants || [];
  const recentBatches = workbench?.recentBatches || [];
  const hasPermission = (permission) => permissions.includes(permission);
  const canRunSelection = hasPermission('selection.run');
  const canReviewSelection = hasPermission('selection.review') || canRunSelection;
  const canManageExports = hasPermission('exports.manage');
  const canManageVetting = hasPermission('vetting.manage');
  const canViewLogs = hasPermission('logs.view');
  const canSeeWorkbench = canManageExports || canManageVetting;

  useEffect(() => {
    const requests = [loadDashboard()];
    if (canSeeWorkbench) {
      requests.push(loadWorkbench());
    }

    void Promise.all(requests);
  }, [canSeeWorkbench]);

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 8,
          overflow: 'hidden',
          position: 'relative',
          background:
            'linear-gradient(160deg, rgba(13,59,102,1) 0%, rgba(21,72,117,0.96) 58%, rgba(141,85,36,0.92) 100%)',
          color: '#fffdf8'
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            width: 360,
            height: 360,
            borderRadius: '50%',
            top: -120,
            right: -90,
            background: 'rgba(255,255,255,0.08)'
          }}
        />
        <Stack spacing={3} sx={{ position: 'relative' }}>
          <Chip
            label="Operational Workspace"
            sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,0.12)', color: 'inherit' }}
          />
          <Typography variant="h2" sx={{ maxWidth: 760, textWrap: 'balance' }}>
            Generate, vet, and govern Barbados jury pools with a complete audit trail.
          </Typography>
          <Typography sx={{ maxWidth: 680, color: 'rgba(255,253,248,0.84)' }}>
            {displayName ? `${displayName}, ` : ''}manage end-to-end jury pool operations from selection through vetting and audit-ready tracking.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {canRunSelection ? (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<HowToVoteRoundedIcon />}
                onClick={runSelection}
                disabled={loading}
              >
                {loading ? 'Generating Pool…' : 'Generate New Pool'}
              </Button>
            ) : null}
            {canManageExports ? (
              <Button
                variant="outlined"
                size="large"
                startIcon={<DownloadRoundedIcon />}
                onClick={createExportBatch}
                disabled={actionLoading === 'export' || !workbench?.latestRun}
                sx={{
                  color: 'inherit',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.5)',
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                }}
              >
                {actionLoading === 'export' ? 'Creating Batch…' : 'Create Police Export Batch'}
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }
        }}
      >
        {(dashboardLoading ? new Array(4).fill(null) : summaryCards).map((card, index) => (
          <Paper
            key={card?.label || index}
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid rgba(13, 59, 102, 0.10)',
              backgroundColor: 'background.paper'
            }}
          >
            {dashboardLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 112 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Typography variant="overline" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h3">{card.value}</Typography>
                <Typography color="text.secondary">{card.detail}</Typography>
              </Stack>
            )}
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: '1fr'
        }}
      >
        <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h3">Policy Snapshot</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                These are the settings captured for the latest run and used for eligibility and vetting workflow behavior.
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              {policyHighlights.map((item) => (
                <Stack
                  key={item.label}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    p: 1.75,
                    borderRadius: 4,
                    bgcolor: 'rgba(13,59,102,0.04)'
                  }}
                >
                  <Typography>{item.label}</Typography>
                  <Chip label={item.value} color="primary" variant="outlined" />
                </Stack>
              ))}
            </Stack>

            <Divider />

            <Box>
              <Typography variant="h6">Latest Pool</Typography>
              {latestPool ? (
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <DetailRow label="Run" value={`#${latestPool.runId}`} />
                  <DetailRow label="Year" value={latestPool.year} />
                  <DetailRow label="Status" value={latestPool.status} />
                  <DetailRow label="Vetting Route" value={latestPool.vettingMode} />
                  <DetailRow label="Snapshot Taken" value={latestPool.settingsSnapshotTakenAtLabel} />
                </Stack>
              ) : (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  No selection run has been recorded yet.
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>

      {canSeeWorkbench ? (
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: { xs: '1fr', xl: '1.35fr 1fr' }
          }}
        >
        <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h3">Police Vetting Workbench</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Manage police vetting for the latest run: create exports, review outcomes, and reconcile imported decisions.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={refreshAll} disabled={workbenchLoading || dashboardLoading}>
                Refresh
              </Button>
            </Stack>

            {workbenchLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : !workbench?.latestRun ? (
              <Alert severity="info">Generate a pool before starting the police vetting workflow.</Alert>
            ) : (
              <>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Paper elevation={0} sx={{ p: 2.5, flex: 1, bgcolor: 'rgba(13,59,102,0.04)' }}>
                    <Typography variant="h6">Active Vetting Run</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                      Run #{workbench.latestRun.runId} from {workbench.latestRun.runDateLabel}
                    </Typography>
                    <Typography color="text.secondary">
                      Mode: {workbench.latestRun.vettingMode}
                    </Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2.5, flex: 1, bgcolor: 'rgba(13,59,102,0.04)' }}>
                    <Typography variant="h6">Recent Batches</Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                      {recentBatches.length ? `${recentBatches.length} tracked export batches` : 'No export batches yet'}
                    </Typography>
                  </Paper>
                </Stack>

                <Divider />

                <Box>
                  <Typography variant="h6">Pending Registrants For In-App Review</Typography>
                  {!canManageVetting ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Your role can review export activity but cannot record in-app police decisions.
                    </Alert>
                  ) : pendingRegistrants.length === 0 ? (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      There are no pending police decisions for the latest run.
                    </Alert>
                  ) : (
                    <TableContainer sx={{ mt: 2 }}>
                      <Table size="small" aria-label="pending registrants">
                        <TableHead>
                          <TableRow>
                            <TableCell>NRN</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Constituency</TableCell>
                            <TableCell>Comment</TableCell>
                            <TableCell align="right">Decision</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingRegistrants.map((person) => (
                            <TableRow key={person.Nrn}>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{person.Nrn}</TableCell>
                              <TableCell>{person.FirstName} {person.LastName}</TableCell>
                              <TableCell>{person.Constituency}</TableCell>
                              <TableCell sx={{ minWidth: 240 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Police comment"
                                  value={vettingDrafts[person.Nrn]?.comments || ''}
                                  onChange={(event) => handleDraftChange(person.Nrn, 'comments', event.target.value)}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    onClick={() => applyInAppDecision(person.Nrn, 'Cleared')}
                                    disabled={actionLoading === `vet-${person.Nrn}-Cleared`}
                                  >
                                    Clear
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => applyInAppDecision(person.Nrn, 'Rejected')}
                                    disabled={actionLoading === `vet-${person.Nrn}-Rejected`}
                                  >
                                    Reject
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6">Batch Import / Export</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    Create export files for police review and import reconciled decisions. Batch records include status, counts, and checksum history.
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {canManageVetting ? (
                      <>
                        <TextField
                          select
                          label="Batch To Import"
                          value={importBatchId}
                          onChange={(event) => setImportBatchId(event.target.value)}
                        >
                          {recentBatches.map((batch) => (
                            <MenuItem key={batch.batchId} value={batch.batchId}>
                              {batch.batchId}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          label="Police Import CSV"
                          multiline
                          minRows={8}
                          value={importCsv}
                          onChange={(event) => setImportCsv(event.target.value)}
                        />
                        <TextField
                          label="Import Shared Secret"
                          type="password"
                          value={importSharedSecret}
                          onChange={(event) => setImportSharedSecret(event.target.value)}
                          autoComplete="off"
                          helperText="This must match the server-side police import shared secret configured in Azure."
                        />
                      </>
                    ) : null}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {canManageVetting ? (
                        <Button
                          variant="contained"
                          startIcon={<FileUploadRoundedIcon />}
                          onClick={importBatchResults}
                          disabled={!importBatchId || !importSharedSecret || actionLoading === 'import'}
                        >
                          {actionLoading === 'import' ? 'Importing…' : 'Apply Imported Decisions'}
                        </Button>
                      ) : null}
                      {canManageExports ? (
                        <Button
                          variant="outlined"
                          startIcon={<DownloadRoundedIcon />}
                          onClick={createExportBatch}
                          disabled={actionLoading === 'export'}
                        >
                          {actionLoading === 'export' ? 'Creating…' : 'Create Another Export Batch'}
                        </Button>
                      ) : null}
                      {exportBatch ? (
                        <Button
                          variant="outlined"
                          startIcon={<DownloadRoundedIcon />}
                          onClick={() => downloadBatchCsv(exportBatch.batchId)}
                          disabled={actionLoading === `download-${exportBatch.batchId}`}
                        >
                          {actionLoading === `download-${exportBatch.batchId}` ? 'Downloading…' : 'Download Latest CSV'}
                        </Button>
                      ) : null}
                    </Stack>
                    {exportBatch ? (
                      <TextField
                        label={`Latest Export Batch ${exportBatch.batchId} (${exportBatch.reconciliationStatus})`}
                        multiline
                        minRows={8}
                        value={exportBatch.csvData}
                        InputProps={{ readOnly: true }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h3">Batch Status</Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Track each export batch, reconciliation progress, and decision totals.
              </Typography>
            </Box>
            {recentBatches.length === 0 ? (
              <Alert severity="info">No police export batches have been created yet.</Alert>
            ) : (
              recentBatches.map((batch) => (
                <Paper key={batch.batchId} elevation={0} sx={{ p: 2, bgcolor: 'rgba(13,59,102,0.04)' }}>
                  <Typography variant="h6">{batch.batchId}</Typography>
                  <Typography color="text.secondary">{batch.exportedAtLabel}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`${batch.registrantCount} exported`} />
                    <Chip size="small" color="primary" variant="outlined" label={batch.reconciliationStatus} />
                    <Chip size="small" color="success" variant="outlined" label={`${batch.clearedCount} cleared`} />
                    <Chip size="small" color="error" variant="outlined" label={`${batch.rejectedCount} rejected`} />
                    <Chip size="small" color="warning" variant="outlined" label={`${batch.pendingCount} pending`} />
                  </Stack>
                  <Typography color="text.secondary" sx={{ mt: 1.5, fontSize: '0.875rem' }}>
                    Export checksum: {batch.exportChecksum ? `${batch.exportChecksum.slice(0, 16)}…` : 'Not recorded'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    Imported decisions: {batch.importedDecisionCount || 0}
                  </Typography>
                  {canManageExports ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadRoundedIcon />}
                      sx={{ mt: 1.5, alignSelf: 'flex-start' }}
                      onClick={() => downloadBatchCsv(batch.batchId)}
                      disabled={actionLoading === `download-${batch.batchId}`}
                    >
                      {actionLoading === `download-${batch.batchId}` ? 'Downloading…' : 'Download CSV'}
                    </Button>
                  ) : null}
                </Paper>
              ))
            )}

            <Divider />

            <GovernanceItem
              icon={<SecurityRoundedIcon color="primary" />}
              title="Tracked Exports"
              description="Each police export batch now carries a stored checksum and reconciliation status for operational traceability."
            />
            <GovernanceItem
              icon={<GppMaybeRoundedIcon color="primary" />}
              title="Later Invalidations"
              description="A juror can become unavailable after police clearance, so keep invalidation and status-history records distinct."
            />
            <GovernanceItem
              icon={<PlaylistAddCheckRoundedIcon color="primary" />}
              title="Recent Activity"
              description={
                canViewLogs && recentActivity.length
                  ? recentActivity.map((item) => `${item.timestampLabel}: ${item.action}`).join(' | ')
                  : (canViewLogs ? 'No audit activity has been recorded yet.' : 'Audit detail is available to roles with log-view permission.')
              }
            />
            <GovernanceItem
              icon={<FactCheckRoundedIcon color="primary" />}
              title="Snapshot-Based Runs"
              description="Each generated pool now stores the policy settings in force at the moment the run was created."
            />
          </Stack>
        </Paper>
      </Box>
      ) : null}

      {canReviewSelection ? (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid rgba(13, 59, 102, 0.10)' }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h3">Latest Generated Registrants</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Preview the latest selected registrants and their current workflow statuses.
            </Typography>
          </Box>

          {selectionResults.length === 0 ? (
            <Alert severity="info">
              Generate a pool to preview NRN, names, constituency, and workflow statuses.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="latest generated registrants">
                <TableHead>
                  <TableRow>
                    <TableCell>NRN</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Constituency</TableCell>
                    <TableCell>Eligibility</TableCell>
                    <TableCell>Vetting</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectionResults.slice(0, 12).map((person) => (
                    <TableRow key={person.nrn}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{person.nrn}</TableCell>
                      <TableCell>{person.firstName}</TableCell>
                      <TableCell>{person.lastName}</TableCell>
                      <TableCell>{person.constituency}</TableCell>
                      <TableCell>
                        <Chip size="small" label={person.eligibilityStatus} color="success" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={person.vettingStatus} color="warning" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Paper>
      ) : null}
    </Stack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
    </Stack>
  );
}

function GovernanceItem({ icon, title, description }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box
        aria-hidden="true"
        sx={{
          width: 42,
          height: 42,
          borderRadius: '14px',
          bgcolor: 'rgba(13,59,102,0.08)',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography color="text.secondary">{description}</Typography>
      </Box>
    </Stack>
  );
}

export default Home;
